import React, { useEffect, useState, createElement } from 'react';
import { ArrowRightIcon, TrendingUpIcon, SearchIcon, StarIcon, ShieldCheckIcon, LayoutIcon, CodeIcon, MousePointerClickIcon, LineChartIcon, BriefcaseIcon, CoffeeIcon, WrenchIcon, ShoppingBagIcon, HeartPulseIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { FinalCtaSection } from '../components/FinalCtaSection';
export function WebsiteLatenMakenPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Website laten maken | Professioneel webdesign | Noord';
    let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement;
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = 'Professionele website laten maken? Noord Digital bouwt conversiegerichte websites voor MKB en ZZP in Noord-Holland. Van strategie tot livegang, inclusief SEO.';
    // Open Graph meta tags
    const ogTags: Record<string, string> = {
      'og:title': 'Website laten maken | Professioneel webdesign | Noord',
      'og:description': 'Professionele website laten maken? Noord Digital bouwt conversiegerichte websites voor MKB en ZZP in Noord-Holland.',
      'og:type': 'website',
      'og:url': 'https://noorddigital.nl/website-laten-maken',
      'og:site_name': 'Noord Digital',
      'og:locale': 'nl_NL'
    };
    Object.entries(ogTags).forEach(([property, content]) => {
      let tag = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.content = content;
    });
  }, []);
  const scrollToContact = (e: React.MouseEvent) => {
    e.preventDefault();
    document.getElementById('contact')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };
  const faqs = [{
    q: 'Wat kost een website laten maken?',
    a: "De kosten voor een website variëren afhankelijk van je wensen, het aantal pagina's en de benodigde functionaliteiten. We bieden altijd een transparante prijsopgave vooraf, zodat je precies weet waar je aan toe bent zonder verborgen kosten."
  }, {
    q: 'Hoe lang duurt het om een website te maken?',
    a: 'Een standaard bedrijfswebsite is vaak binnen 2 tot 4 weken live. Voor complexere websites of maatwerk oplossingen plannen we samen een realistische tijdlijn in.'
  }, {
    q: 'Kan ik zelf de website aanpassen?',
    a: 'Ja, wij bouwen onze websites met gebruiksvriendelijke systemen (zoals WordPress). Je krijgt na oplevering uitleg zodat je zelf eenvoudig teksten en afbeeldingen kunt aanpassen.'
  }, {
    q: 'Is SEO inbegrepen?',
    a: 'Absoluut. Elke website die wij opleveren is technisch geoptimaliseerd voor zoekmachines. We zorgen voor de juiste structuur, snelle laadtijden en basis on-page SEO zodat je direct goed vindbaar bent.'
  }, {
    q: 'Wat gebeurt er na oplevering?',
    a: 'Na livegang laten we je niet in de steek. We bieden betrouwbare hosting en technisch onderhoud aan, zodat je website altijd snel, veilig en up-to-date blijft.'
  }];
  return <main className="pt-16 lg:pt-[72px]" data-id="element-2647">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 bg-navy-900 overflow-hidden" data-id="element-2648">
        <div className="absolute inset-0 pointer-events-none" data-id="element-2649">
          <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full opacity-20 blur-3xl bg-accent" data-id="element-2650" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full opacity-10 blur-3xl bg-accent-light" data-id="element-2651" />
          <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '32px 32px'
        }} data-id="element-2652" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2653">
          {/* Breadcrumb */}
          <nav className="flex items-center justify-center gap-2 text-sm mb-8 animate-fade-in-up" data-id="element-2654">
            <a href="#/" className="text-navy-400 hover:text-white transition-colors" data-id="element-2655">
              Home
            </a>
            <span className="text-navy-500" data-id="element-2656">/</span>
            <span className="text-accent-light font-medium" data-id="element-2657">
              Website laten maken
            </span>
          </nav>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6 animate-fade-in-up" data-id="element-2658">
            Website laten maken die klanten oplevert
          </h1>
          <p className="text-lg lg:text-xl text-navy-200 max-w-2xl mx-auto mb-10 animate-fade-in-up-delay-1" data-id="element-2659">
            Professionele websites voor bedrijven die online willen groeien. Wij
            ontzorgen je volledig, van strategie tot livegang.
          </p>
          <div className="animate-fade-in-up-delay-2" data-id="element-2660">
            <a href="#contact" onClick={scrollToContact} className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white font-bold px-8 py-4 rounded-button shadow-medium hover:shadow-card transition-all duration-200 text-lg" data-id="element-2661">
              Plan een gesprek
              <ArrowRightIcon className="w-5 h-5" data-id="element-2662" />
            </a>
          </div>
        </div>
      </section>

      {/* Waarom Section */}
      <section className="py-24 bg-surface-muted" data-id="element-2663">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2664">
          <div className="text-center mb-16" data-id="element-2665">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-2666">
              Waarom een goede website belangrijk is
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-2667">
              Een sterke online aanwezigheid is onmisbaar voor de groei van jouw
              bedrijf.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-2668">
            <div className="bg-white p-8 rounded-card shadow-soft hover:shadow-medium transition-shadow" data-id="element-2669">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-2670">
                <TrendingUpIcon className="w-6 h-6 text-accent" data-id="element-2671" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2672">
                Meer aanvragen
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2673">
                Zet websitebezoekers om in betalende klanten met een
                conversiegerichte aanpak.
              </p>
            </div>
            <div className="bg-white p-8 rounded-card shadow-soft hover:shadow-medium transition-shadow" data-id="element-2674">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-2675">
                <SearchIcon className="w-6 h-6 text-accent" data-id="element-2676" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2677">
                Betere vindbaarheid
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2678">
                Word lokaal en regionaal beter gevonden in Google door
                potentiële klanten.
              </p>
            </div>
            <div className="bg-white p-8 rounded-card shadow-soft hover:shadow-medium transition-shadow" data-id="element-2679">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-2680">
                <StarIcon className="w-6 h-6 text-accent" data-id="element-2681" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2682">
                Professionele uitstraling
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2683">
                Maak een onuitwisbare eerste indruk met een modern en strak
                webdesign.
              </p>
            </div>
            <div className="bg-white p-8 rounded-card shadow-soft hover:shadow-medium transition-shadow" data-id="element-2684">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-2685">
                <ShieldCheckIcon className="w-6 h-6 text-accent" data-id="element-2686" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2687">
                Meer vertrouwen
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2688">
                Straal autoriteit uit en overtuig bezoekers van jouw expertise
                en kwaliteit.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Wat wij bouwen */}
      <section className="py-24 bg-white" data-id="element-2689">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2690">
          <div className="text-center mb-16" data-id="element-2691">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-2692">
              Wat wij bouwen
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-2693">
              Oplossingen op maat voor jouw specifieke bedrijfsdoelen.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-2694">
            <div className="border border-navy-100 p-8 rounded-card hover:border-accent/30 transition-colors" data-id="element-2695">
              <div className="flex items-start gap-4" data-id="element-2696">
                <div className="w-12 h-12 rounded-xl bg-navy-50 flex items-center justify-center flex-shrink-0" data-id="element-2697">
                  <LayoutIcon className="w-6 h-6 text-navy-700" data-id="element-2698" />
                </div>
                <div data-id="element-2699">
                  <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-2700">
                    Bedrijfswebsite
                  </h3>
                  <p className="text-slate-600" data-id="element-2701">
                    Een compleet online visitekaartje dat jouw diensten,
                    projecten en bedrijfscultuur perfect presenteert.
                  </p>
                </div>
              </div>
            </div>
            <div className="border border-navy-100 p-8 rounded-card hover:border-accent/30 transition-colors" data-id="element-2702">
              <div className="flex items-start gap-4" data-id="element-2703">
                <div className="w-12 h-12 rounded-xl bg-navy-50 flex items-center justify-center flex-shrink-0" data-id="element-2704">
                  <CodeIcon className="w-6 h-6 text-navy-700" data-id="element-2705" />
                </div>
                <div data-id="element-2706">
                  <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-2707">
                    WordPress website
                  </h3>
                  <p className="text-slate-600" data-id="element-2708">
                    Flexibele, makkelijk te beheren websites gebouwd op het
                    meest populaire CMS ter wereld.
                  </p>
                </div>
              </div>
            </div>
            <div className="border border-navy-100 p-8 rounded-card hover:border-accent/30 transition-colors" data-id="element-2709">
              <div className="flex items-start gap-4" data-id="element-2710">
                <div className="w-12 h-12 rounded-xl bg-navy-50 flex items-center justify-center flex-shrink-0" data-id="element-2711">
                  <MousePointerClickIcon className="w-6 h-6 text-navy-700" data-id="element-2712" />
                </div>
                <div data-id="element-2713">
                  <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-2714">
                    Landingspagina's
                  </h3>
                  <p className="text-slate-600" data-id="element-2715">
                    Doelgerichte pagina's speciaal ontworpen voor
                    advertentiecampagnes en maximale leadgeneratie.
                  </p>
                </div>
              </div>
            </div>
            <div className="border border-navy-100 p-8 rounded-card hover:border-accent/30 transition-colors" data-id="element-2716">
              <div className="flex items-start gap-4" data-id="element-2717">
                <div className="w-12 h-12 rounded-xl bg-navy-50 flex items-center justify-center flex-shrink-0" data-id="element-2718">
                  <LineChartIcon className="w-6 h-6 text-navy-700" data-id="element-2719" />
                </div>
                <div data-id="element-2720">
                  <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-2721">
                    Conversie geoptimaliseerde websites
                  </h3>
                  <p className="text-slate-600" data-id="element-2722">
                    Websites waarbij elke knop, tekst en afbeelding is ingericht
                    om de bezoeker tot actie te laten overgaan.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Proces */}
      <section className="py-24 bg-navy-900 relative overflow-hidden" data-id="element-2723">
        <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
        backgroundSize: '40px 40px'
      }} data-id="element-2724" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2725">
          <div className="text-center mb-16" data-id="element-2726">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4" data-id="element-2727">
              Ons proces
            </h2>
            <p className="text-lg text-navy-300 max-w-2xl mx-auto" data-id="element-2728">
              In 5 heldere stappen naar jouw nieuwe website.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-6" data-id="element-2729">
            {['Kennismaking', 'Strategie', 'Ontwerp', 'Ontwikkeling', 'Livegang'].map((step, i) => <div key={step} className="bg-white/[0.06] border border-white/10 p-6 rounded-2xl text-center relative" data-id="element-2730">
                <span className="absolute top-2 right-4 text-4xl font-bold text-white/[0.05]" data-id="element-2731">
                  {i + 1}
                </span>
                <div className="w-10 h-10 rounded-full bg-accent/20 text-accent flex items-center justify-center mx-auto mb-4 font-bold" data-id="element-2732">
                  {i + 1}
                </div>
                <h3 className="text-white font-bold" data-id="element-2733">{step}</h3>
              </div>)}
          </div>
        </div>
      </section>

      {/* Voor wie */}
      <section className="py-24 bg-surface-muted" data-id="element-2734">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2735">
          <div className="text-center mb-16" data-id="element-2736">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-2737">
              Voor wie bouwen wij?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-2738">
              Wij hebben ervaring in diverse sectoren.
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-4" data-id="element-2739">
            <div className="bg-white px-6 py-4 rounded-xl shadow-soft flex items-center gap-3" data-id="element-2740">
              <BriefcaseIcon className="w-5 h-5 text-accent" data-id="element-2741" />
              <span className="font-medium text-navy-900" data-id="element-2742">
                Zakelijke dienstverlening
              </span>
            </div>
            <div className="bg-white px-6 py-4 rounded-xl shadow-soft flex items-center gap-3" data-id="element-2743">
              <CoffeeIcon className="w-5 h-5 text-accent" data-id="element-2744" />
              <span className="font-medium text-navy-900" data-id="element-2745">Horeca</span>
            </div>
            <div className="bg-white px-6 py-4 rounded-xl shadow-soft flex items-center gap-3" data-id="element-2746">
              <WrenchIcon className="w-5 h-5 text-accent" data-id="element-2747" />
              <span className="font-medium text-navy-900" data-id="element-2748">
                Bouw & installatie
              </span>
            </div>
            <div className="bg-white px-6 py-4 rounded-xl shadow-soft flex items-center gap-3" data-id="element-2749">
              <ShoppingBagIcon className="w-5 h-5 text-accent" data-id="element-2750" />
              <span className="font-medium text-navy-900" data-id="element-2751">Retail</span>
            </div>
            <div className="bg-white px-6 py-4 rounded-xl shadow-soft flex items-center gap-3" data-id="element-2752">
              <HeartPulseIcon className="w-5 h-5 text-accent" data-id="element-2753" />
              <span className="font-medium text-navy-900" data-id="element-2754">Zorg</span>
            </div>
          </div>
        </div>
      </section>

      {/* Gerelateerde pagina's */}
      <section className="py-16 bg-white border-b border-navy-100" data-id="element-2755">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2756">
          <h3 className="text-lg font-semibold text-navy-900 mb-6" data-id="element-2757">
            Ontdek meer:
          </h3>
          <div className="flex flex-wrap justify-center gap-4" data-id="element-2758">
            <a href="#/wordpress-websites" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-2759">
              WordPress website laten bouwen
            </a>
            <span className="text-navy-200 hidden sm:inline" data-id="element-2760">•</span>
            <a href="#/kosten-website-laten-maken" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-2761">
              Wat kost een website?
            </a>
            <span className="text-navy-200 hidden sm:inline" data-id="element-2762">•</span>
            <a href="#/webdesign-alkmaar" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-2763">
              Webdesigner in Alkmaar
            </a>
            <span className="text-navy-200 hidden sm:inline" data-id="element-2764">•</span>
            <a href="#/hosting-onderhoud" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-2765">
              Hosting & technisch beheer
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-surface-muted" data-id="element-2766">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2767">
          <div className="text-center mb-16" data-id="element-2768">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-2769">
              Veelgestelde vragen
            </h2>
          </div>

          <div className="space-y-4" data-id="element-2770">
            {faqs.map((faq, index) => <div key={index} className="bg-white rounded-xl shadow-soft overflow-hidden" data-id="element-2771">
                <button onClick={() => toggleFaq(index)} className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none" data-id="element-2772">
                  <span className="font-bold text-navy-900" data-id="element-2773">{faq.q}</span>
                  {openFaq === index ? <ChevronUpIcon className="w-5 h-5 text-accent flex-shrink-0" data-id="element-2774" /> : <ChevronDownIcon className="w-5 h-5 text-navy-400 flex-shrink-0" data-id="element-2775" />}
                </button>
                {openFaq === index && <div className="px-6 pb-5 text-slate-600 leading-relaxed" data-id="element-2776">
                    {faq.a}
                  </div>}
              </div>)}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <FinalCtaSection data-id="element-2777" />
    </main>;
}